import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Activity, 
  Clock, 
  AlertTriangle, 
  Server, 
  RefreshCw,
  Database,
  Cpu,
  HardDrive,
  TrendingUp,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const MonitoringAdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/admin/monitoring?period=${period}`);
      setData(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch monitoring data:', error);
      if (error.response?.status === 403) {
        toast.error('Přístup zamítnut - pouze pro administrátory');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [period, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleClearBuffer = async () => {
    if (!window.confirm('Opravdu chcete vymazat buffer metrik?')) return;
    
    try {
      await api.delete('/admin/monitoring/clear-buffer');
      toast.success('Buffer vymazán');
      fetchData();
    } catch (error) {
      toast.error('Nepodařilo se vymazat buffer');
    }
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('cs-CZ');
  };

  const getStatusColor = (code) => {
    if (code >= 500) return 'bg-red-500';
    if (code >= 400) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getOperationIcon = (type) => {
    switch (type) {
      case 'import': return <Database className="w-4 h-4" />;
      case 'export': return <HardDrive className="w-4 h-4" />;
      case 'write': return <Activity className="w-4 h-4" />;
      default: return <Server className="w-4 h-4" />;
    }
  };

  if (!user?.permissions?.includes('admin')) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Přístup zamítnut</h2>
        <p className="text-slate-600 mt-2">Tato stránka je dostupná pouze pro administrátory.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-slate-600">Načítání dat...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin')}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zpět
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Activity className="w-6 h-6 text-blue-600" />
                Server Monitoring
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Poslední aktualizace: {lastUpdate ? formatTimestamp(lastUpdate.toISOString()) : '-'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Period selector */}
            <div className="flex bg-white rounded-lg border p-1">
              {['24h', '7d', '30d'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    period === p 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {p === '24h' ? '24 hodin' : p === '7d' ? '7 dní' : '30 dní'}
                </button>
              ))}
            </div>
            
            {/* Auto-refresh toggle */}
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto (30s)' : 'Manuální'}
            </Button>
            
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Obnovit
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleClearBuffer} className="text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" />
              Vymazat buffer
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Celkem požadavků</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {data?.total_requests?.toLocaleString() || 0}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Paměť (RSS)</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {data?.memory?.current?.process_rss_mb || 0} MB
                  </p>
                </div>
                <Cpu className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Systém RAM</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {data?.memory?.current?.system_percent || 0}%
                  </p>
                </div>
                <Server className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Chyby</p>
                  <p className="text-2xl font-bold text-red-600">
                    {data?.errors?.reduce((sum, e) => sum + e.count, 0) || 0}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Memory Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Využití paměti v čase
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.memory?.history?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={data.memory.history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={(ts) => {
                      const d = new Date(ts);
                      return `${d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}`;
                    }}
                    stroke="#94a3b8"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#94a3b8" 
                    fontSize={12}
                    tickFormatter={(v) => `${v} MB`}
                  />
                  <Tooltip 
                    labelFormatter={(ts) => formatTimestamp(ts)}
                    formatter={(value) => [`${value} MB`, 'RSS']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="rss_mb" 
                    stroke="#8b5cf6" 
                    fill="#c4b5fd" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Zatím nejsou k dispozici data o paměti
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Slowest Endpoints */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Nejpomalejší endpointy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {data?.endpoints?.slice(0, 15).map((ep, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs shrink-0">
                          {ep.method}
                        </Badge>
                        <span className="text-sm font-mono text-slate-700 truncate">
                          {ep.endpoint}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span>{ep.total_calls} volání</span>
                        {ep.error_count > 0 && (
                          <span className="text-red-500">{ep.error_count} chyb</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <div className={`font-semibold ${ep.avg_duration_ms > 1000 ? 'text-red-600' : ep.avg_duration_ms > 500 ? 'text-amber-600' : 'text-green-600'}`}>
                        {formatDuration(ep.avg_duration_ms)}
                      </div>
                      <div className="text-xs text-slate-400">průměr</div>
                    </div>
                  </div>
                ))}
                {(!data?.endpoints || data.endpoints.length === 0) && (
                  <p className="text-center text-slate-400 py-8">Žádná data</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Errors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Přehled chyb
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {data?.errors?.map((err, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={`${getStatusColor(err.status_code)} text-white`}>
                          {err.status_code}
                        </Badge>
                        <span className="font-semibold text-slate-900">
                          {err.count}x
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        Poslední: {formatTimestamp(err.last_occurrence)}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {err.top_paths?.slice(0, 3).map((p, pidx) => (
                        <div key={pidx} className="flex items-center justify-between text-xs">
                          <span className="font-mono text-slate-600 truncate">{p.path}</span>
                          <span className="text-slate-400 ml-2">{p.count}x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {(!data?.errors || data.errors.length === 0) && (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Activity className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-slate-500">Žádné chyby v tomto období</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Operations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Vytížení podle operací
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data?.operations?.map((op, idx) => (
                <div 
                  key={idx}
                  className="p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {getOperationIcon(op.type)}
                    <span className="font-semibold text-slate-900 capitalize">
                      {op.type === 'read' ? 'Čtení' : 
                       op.type === 'write' ? 'Zápis' :
                       op.type === 'import' ? 'Import' :
                       op.type === 'export' ? 'Export' : op.type}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Počet:</span>
                      <span className="font-medium">{op.count.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Průměr:</span>
                      <span className="font-medium">{formatDuration(op.avg_duration_ms)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Celkem:</span>
                      <span className="font-medium">{formatDuration(op.total_duration_ms)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {(!data?.operations || data.operations.length === 0) && (
                <div className="col-span-4 text-center py-8 text-slate-400">
                  Žádná data o operacích
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Slow Requests */}
        {data?.slow_requests?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-600" />
                Pomalé požadavky (&gt;2s)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Čas</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Metoda</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Endpoint</th>
                      <th className="text-right py-2 px-3 text-slate-500 font-medium">Doba</th>
                      <th className="text-right py-2 px-3 text-slate-500 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.slow_requests.slice(0, 20).map((req, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-3 text-slate-600">
                          {formatTimestamp(req.created_at)}
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline">{req.method}</Badge>
                        </td>
                        <td className="py-2 px-3 font-mono text-slate-700 max-w-xs truncate">
                          {req.path}
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-red-600">
                          {formatDuration(req.duration_ms)}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Badge className={`${getStatusColor(req.status_code)} text-white`}>
                            {req.status_code}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Buffer Info */}
        <div className="text-center text-sm text-slate-400">
          Buffer obsahuje {data?.buffer_size?.toLocaleString() || 0} záznamů
        </div>
      </div>
    </div>
  );
};

export default MonitoringAdminPage;
